import React, { useEffect, useState, useMemo, useContext, useCallback } from 'react';

import { useParams } from 'react-router-dom';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import PaginationItem from '@mui/material/PaginationItem';

import { SearchX } from 'lucide-react';

import Reveal from '../../Components/Reveal';
import { getCategoryIcon } from '../../Helpers/categoryIcon';

import Axios from '../../Assets/Axios/Axios';

import ToastContext from '../../Context/ToastContext';

import { COLORS } from '../../Constant/COLORS';

import Header from '../../Components/Header';
import Footer from '../../Components/Footer';

import Card, { SkeletonCard } from './Components/Card';

import { toCapitalize } from '../../Helpers';
import getThumbnailImage from '../../Helpers/getThumbnailImage';

import { FindServicesByCategorySlug } from '../../Server/Query/index';

const Category = () => {
  const { showToastMessage } = useContext(ToastContext);

  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageNo, setPageNo] = useState(1);

  const { slug } = useParams();

  const categoryTitle = (slug ?? '').replace(/-/g, ' ');

  const servicesToShow = useMemo(() => {
    const lastItem = pageNo * 12;
    return services.slice(lastItem - 12, lastItem);
  }, [pageNo, services]);

  const fetchServices = useCallback(
    async categorySlug => {
      setIsLoading(true);
      try {
        const { data, errors } = await Axios(
          FindServicesByCategorySlug({ slug: categorySlug })
        );

        if (errors || !data?.Services) {
          showToastMessage('Oops! Something went wrong!, Failed to load services');
          setServices([]);
          return;
        }

        setServices(
          data.Services.map(service => ({
            ...service,
            banner_image: service.banner_image ?? getThumbnailImage(service.name),
          }))
        );
      } catch {
        showToastMessage('Oops! Something went wrong!, Failed to load services');
        setServices([]);
      } finally {
        setIsLoading(false);
      }
    },
    [showToastMessage]
  );

  useEffect(() => {
    setPageNo(1);
    fetchServices(slug);
  }, [fetchServices, slug]);

  function getServiceCount() {
    const count = services.length;
    return `${count} service${count === 1 ? '' : 's'} available.`;
  }

  return (
    <>
      <Header isSellerOrCategoryPage />
      <Divider style={{ borderColor: COLORS.hairline }} />
      <Box maxWidth='80%' mx='auto' mt={{ xs: 3, md: 5 }} mb={8}>
        <Reveal>
          <Stack direction='row' alignItems='center' gap={2.5} mb={1}>
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${COLORS.teal}, ${COLORS.tealDark})`,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {(() => {
                const Icon = getCategoryIcon(categoryTitle);
                return <Icon size={30} strokeWidth={1.6} />;
              })()}
            </Box>
            <Box>
              <Typography variant='overline' color={COLORS.teal} display='block'>
                Category
              </Typography>
              <Typography variant='h3'>{toCapitalize(categoryTitle)}</Typography>
            </Box>
          </Stack>
          <Box
            component='span'
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              mb: 5,
              borderRadius: 999,
              bgcolor: COLORS.surfaceAlt,
              border: `1px solid ${COLORS.hairline}`,
              color: COLORS.inkSoft,
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            {getServiceCount()}
          </Box>
        </Reveal>

        <Box
          display='grid'
          gap='2em'
          gridTemplateColumns={{
            xs: '1fr',
            sm: 'repeat(2,1fr)',
            md: 'repeat(3,1fr)',
          }}>
          {isLoading && (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          )}

          {!isLoading &&
            servicesToShow.length > 0 &&
            servicesToShow.map((service, index) => (
              <Reveal
                key={service.id}
                delay={Math.min(index, 6) * 0.06}
                style={{ height: '100%' }}>
                <Card
                  service={service}
                  href={`/professional/${service.service_id ?? service.serviceId ?? service.id}`}
                />
              </Reveal>
            ))}
        </Box>

        {!isLoading && servicesToShow.length === 0 && (
          <Reveal>
            <Stack
              alignItems='center'
              textAlign='center'
              gap={1.5}
              py={8}
              px={3}
              sx={{
                border: `1px dashed ${COLORS.hairline}`,
                borderRadius: 4,
                bgcolor: COLORS.surface,
              }}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: '50%',
                  bgcolor: COLORS.surfaceAlt,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <SearchX size={30} color={COLORS.inkSoft} />
              </Box>
              <Typography variant='h5'>No services here yet</Typography>
              <Typography color={COLORS.inkSoft} maxWidth={380}>
                We couldn&apos;t find any professionals in this category right
                now. Try exploring another category.
              </Typography>
            </Stack>
          </Reveal>
        )}

        {services.length > 12 && (
          <Pagination
            page={pageNo}
            count={Math.ceil(services.length / 12)}
            hideNextButton
            hidePrevButton
            onChange={(_e, pageNo) => {
              setPageNo(pageNo);
              window.scrollTo({ behavior: 'smooth', top: 0 });
            }}
            sx={{
              '.MuiPagination-ul': {
                justifyContent: 'center',
                gap: '1em',
              },
              mt: '2em',
            }}
            renderItem={pagItemProps => (
              <PaginationItem
                {...pagItemProps}
                style={{
                  color: pagItemProps.selected ? 'white' : COLORS.teal,
                  backgroundColor: pagItemProps.selected ? COLORS.teal : 'transparent',
                  border: pagItemProps.selected ? 'none' : `1px solid ${COLORS.hairline}`,
                  fontWeight: 600,
                }}
              />
            )}
          />
        )}
      </Box>
      <Footer />
    </>
  );
};

export default Category;
